<template>
  <v-app>
    <v-snackbar v-model="snackbar" :timeout="3000" color="primary">
      {{ snackbarText }}
    </v-snackbar>
    <FloatingWindow
        :window-id="'menu-window-' + mapName"
        title="基本設定"
        :default-width="330"
        :default-height="550"
        :default-top="65"
        :default-left="10"
        :is-modal="isSmallScreen"
        :use-fixed-position="true"
        @close="handleClose"
    >
    <div class="menu-div">
      <!-- ヘッダー -->
      <div class="menu-header">
        <div class="header-left">
          <span class="version-badge">v{{ clientVersion }}</span>
          <span v-if="myNickname" class="nickname">{{ myNickname }}</span>
        </div>
        <v-btn size="small" variant="text" prepend-icon="mdi-reload" @click="reloadPage">
          再読み込み
        </v-btn>
      </div>

      <!-- タブ -->
      <v-tabs v-model="menuTab" density="compact" color="primary" grow class="menu-tabs">
        <v-tab value="usage"><v-icon size="small" class="mr-1">mdi-star</v-icon>プラン</v-tab>
        <v-tab value="android"><v-icon size="small" class="mr-1">mdi-android</v-icon>Android</v-tab>
        <v-tab value="display"><v-icon size="small" class="mr-1">mdi-monitor</v-icon>表示</v-tab>
        <v-tab value="control"><v-icon size="small" class="mr-1">mdi-gesture-swipe</v-icon>操作</v-tab>
        <v-tab value="print"><v-icon size="small" class="mr-1">mdi-printer</v-icon>印刷</v-tab>
        <v-tab value="draw"><v-icon size="small" class="mr-1">mdi-draw</v-icon>ドロー</v-tab>
        <v-tab value="other"><v-icon size="small" class="mr-1">mdi-cog</v-icon>その他</v-tab>
      </v-tabs>

      <!-- タブコンテンツ -->
      <v-tabs-window v-model="menuTab" class="tab-content">
        <!-- 表示タブ -->
        <v-tabs-window-item value="display">
          <div class="tab-panel">
            <v-switch class="menu-switch" v-model="s_isPitch" @change="changePitch" label="２画面時に傾きを同期" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_isWindow" label="ウインドウ復帰" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_mapillary" label="mapillary" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_isContextMenu" label="右クリックメニュー" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_showCrosshair" label="画面中心の十字" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_showNorthArrow" label="方位記号" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_isFullScreenPopup" label="全画面ポップアップ" color="primary" hide-details density="compact" />
            <v-switch class="menu-switch" v-model="s_isSplash" @update:modelValue="onToggleSplash" label="スプラッシュ" color="primary" hide-details density="compact" />
            <template v-if="isSmallScreen">
              <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e0e0e0;">
                <v-switch class="menu-switch" v-model="s_showPcMode" @update:modelValue="onTogglePcMode" label="PC状態で表示" color="primary" hide-details density="compact" />
                <div style="font-size: 11px; color: #999; margin-top: 2px; padding-left: 4px;">ONにするとヘルプ・情報ボタン等を表示します</div>
              </div>
            </template>
          </div>
        </v-tabs-window-item>

        <!-- 操作タブ -->
        <v-tabs-window-item value="control">
          <div class="tab-panel">
            <div class="subsection-label">マップ操作速度</div>
            <template v-if="isAndroid">
              <select v-model="s_mapSpeedProfile" class="native-select">
                <option v-for="option in mapSpeedOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </template>
            <v-select v-else
                      v-model="s_mapSpeedProfile"
                      :items="mapSpeedOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :menu-props="{
                        maxHeight: '350px',
                        overflow: 'scroll',
                        contentClass: 'scrollable-menu'
                      }"
            ></v-select>

            <v-divider class="my-4"></v-divider>

            <div class="subsection-label">標高強調: <strong>{{ s_terrainLevel }}倍</strong></div>
            <v-slider
              v-model="s_terrainLevel"
              :min="1"
              :max="10"
              :step="0.1"
              hide-details
              color="primary"
              @update:modelValue="terrainLevelInput"
            ></v-slider>
          </div>
        </v-tabs-window-item>

        <!-- 印刷タブ -->
        <v-tabs-window-item value="print">
          <div class="tab-panel">
            <div v-for="map in activePrintMaps" :key="map" class="oh3-mb-2">
              <v-textarea
                  :model-value="$store.state.printTitleTexts[map]"
                  @update:model-value="updateMapTitle(map, $event)"
                  :label="activePrintMaps.length === 1 ? 'タイトル' : printMapLabels[map]"
                  auto-grow
                  rows="1"
                  variant="outlined"
                  density="compact"
                  hide-details
              />
              <v-select
                v-if="getMapLayers(map).length > 0"
                :items="getMapLayers(map)"
                item-title="label"
                item-value="label"
                :label="'レイヤーから入力 (' + (activePrintMaps.length === 1 ? '' : map.replace('map0','Map')) + ')'"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mt-1"
                @update:modelValue="insertLayerName(map, $event)"
              />
            </div>
            <!-- フォントサイズと色を一列に -->
            <div class="draw-config-row oh3-mb-2">
              <label class="vlike vlike--floating vlike--compact" style="flex: 1 1 0; min-width: 0;">
                <span class="vlike__text">フォントサイズ</span>
                <select
                    v-model="s_textPx"
                    class="vlike__control oh3-text-md"
                    :data-empty="s_textPx === ''"
                    required
                    @change="configChange('font-size', s_textPx)"
                >
                  <option value="" disabled>選択してください</option>
                  <option v-for="n in 101" :key="n-1" :value="n-1">{{ n-1 }}</option>
                </select>
              </label>

              <template v-if="isSmallScreen">
                <div v-if="isAndroid" class="native-select-wrapper" style="flex: 1 1 0; min-width: 0;">
                  <label class="native-select-label" for="android-color-select">色</label>
                  <select
                      id="android-color-select"
                      v-model="s_titleColor"
                      class="native-select"
                      @change="configChange('fill-color',s_titleColor)">
                    <option v-for="item in titleColors" :key="item.color" :value="item.color">
                      {{ item.label }}
                    </option>
                  </select>
                </div>
                <v-select
                    v-else
                    v-model="s_titleColor"
                    :items="titleColors"
                    item-title="label"
                    item-value="color"
                    label="色"
                    variant="outlined"
                    density="compact"
                    hide-details
                    :menu-props="{
                      maxHeight: '350px',
                      overflow: 'scroll',
                      contentClass: 'scrollable-menu'
                    }"
                    @update:modelValue="configChange('fill-color',s_titleColor)"
                    class="draw-config-compact-select"
                    style="flex: 1 1 0; min-width: 0;"
                />
              </template>
              <v-select
                  v-else
                  v-model="s_titleColor"
                  :items="titleColors"
                  item-title="label"
                  item-value="color"
                  label="色"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :menu-props="{
                    maxHeight: '350px',
                    overflow: 'scroll',
                    contentClass: 'scrollable-menu'
                  }"
                  @update:modelValue="configChange('fill-color',s_titleColor)"
                  class="draw-config-compact-select"
                  style="flex: 1 1 0; min-width: 0;"
              />
            </div>

            <!-- 日付入力 -->
            <v-text-field
                v-model="s_printDate"
                label="日付"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                class="oh3-mb-2"
                @update:modelValue="configChange('print-date', s_printDate)"
            />

            <template v-if="isSmallScreen">
              <div v-if="isAndroid" class="native-select-wrapper">
                <label class="native-select-label" for="android-scale-select">縮尺率固定</label>
                <select
                    id="android-scale-select"
                    v-model="titleScale"
                    class="native-select"
                    @change="setZoom()">
                  <option v-for="item in titleScales" :key="item.zoom" :value="item.zoom">
                    {{ item.label }}
                  </option>
                </select>
              </div>
              <v-select
                  v-else
                  v-model="titleScale"
                  :items="titleScales"
                  item-title="label"
                  item-value="zoom"
                  label="縮尺率固定"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="oh3-mb-3"
                  :menu-props="{
                    maxHeight: '350px',
                    overflow: 'scroll',
                    contentClass: 'scrollable-menu'
                  }"
                  @update:modelValue="setZoom()"
              />
            </template>
            <v-select
                v-else
                v-model="titleScale"
                :items="titleScales"
                item-title="label"
                item-value="zoom"
                label="縮尺率固定"
                variant="outlined"
                density="compact"
                hide-details
                class="oh3-mb-3"
                :menu-props="{
                  maxHeight: '350px',
                  overflow: 'scroll',
                  contentClass: 'scrollable-menu'
                }"
                @update:modelValue="setZoom()"
            />
            <template v-if="s_isPrint">
              <template v-if="isSmallScreen">
                <div v-if="isAndroid" class="native-select-wrapper">
                  <label class="native-select-label" for="android-direction-select">印刷方向</label>
                  <select
                      id="android-direction-select"
                      v-model="s_titleDirection"
                      class="native-select"
                      @change="configChange('direction',s_titleDirection)">
                    <option v-for="item in titleDirections" :key="item.direction" :value="item.direction">
                      {{ item.label }}
                    </option>
                  </select>
                </div>
                <v-select
                    v-else
                    v-model="s_titleDirection"
                    :items="titleDirections"
                    item-title="label"
                    item-value="direction"
                    label="印刷方向"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="oh3-mb-3"
                    :menu-props="{
                      maxHeight: '350px',
                      overflow: 'scroll',
                      contentClass: 'scrollable-menu'
                    }"
                    @update:modelValue="configChange('direction',s_titleDirection)"
                />
              </template>
              <v-select
                  v-else
                  v-model="s_titleDirection"
                  :items="titleDirections"
                  item-title="label"
                  item-value="direction"
                  label="印刷方向"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="oh3-mb-3"
                  :menu-props="{
                    maxHeight: '350px',
                    overflow: 'scroll',
                    contentClass: 'scrollable-menu'
                  }"
                  @update:modelValue="configChange('direction',s_titleDirection)"
              />
            </template>

            <!-- === PDF Pro 設定セクション START（試行錯誤中：このtemplateブロックをコメントアウトで無効化可能）===
            <template v-if="s_isPrint">
              <div class="section-header oh3-mt-3">法定図面PDF設定</div>
              <v-switch
                  class="draw-config-switch"
                  v-model="pdfProShowBorder"
                  @update:modelValue="onPdfProOptionChange"
                  label="図郭（枠線）を表示"
                  color="primary"
                  hide-details
              />
              <v-switch
                  class="draw-config-switch"
                  v-model="pdfProShowNorthArrow"
                  @update:modelValue="onPdfProOptionChange"
                  label="北矢印を表示"
                  color="primary"
                  hide-details
              />
              <v-switch
                  class="draw-config-switch"
                  v-model="pdfProShowFooter"
                  @update:modelValue="onPdfProOptionChange"
                  label="フッター情報を表示"
                  color="primary"
                  hide-details
              />
              <v-switch
                  class="draw-config-switch"
                  v-model="pdfProShowChibanList"
                  @update:modelValue="onPdfProOptionChange"
                  label="地番一覧を2ページ目に追加"
                  color="primary"
                  hide-details
              />
              <v-text-field
                  v-model="pdfProAuthorName"
                  label="作成者名"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="oh3-mb-2 oh3-mt-2"
                  @update:modelValue="onPdfProOptionChange"
              />
            </template>
            === PDF Pro 設定セクション END === -->

            <!-- プリセット機能 -->
            <div class="oh3-mt-3">
              <div class="draw-config-preset-row oh3-mb-2">
                <input
                  v-model="presetName"
                  placeholder="プリセット名を入力"
                  class="draw-config-preset-input"
                />
                <v-btn v-track="'preset-save'" @click="savePreset" color="primary">保存</v-btn>
              </div>
              <div v-if="savedPresets.length > 0" class="draw-config-preset-row oh3-mt-3">
                <template v-if="isSmallScreen">
                  <div v-if="isAndroid" class="native-select-wrapper draw-config-flex">
                    <label class="native-select-label" for="android-preset-select">プリセット選択</label>
                    <select
                        id="android-preset-select"
                        v-model="selectedPreset"
                        class="native-select">
                      <option value="">プリセットを選択...</option>
                      <option v-for="preset in savedPresets" :key="preset.name" :value="preset.name">
                        {{ preset.name }}
                      </option>
                    </select>
                  </div>
                  <v-select
                      v-else
                      v-model="selectedPreset"
                      :items="savedPresetItems"
                      item-title="label"
                      item-value="value"
                      label="プリセット選択"
                      variant="outlined"
                      density="compact"
                      :menu-props="{
                        maxHeight: '350px',
                        overflow: 'scroll',
                        contentClass: 'scrollable-menu'
                      }"
                      class="draw-config-flex"
                  />
                </template>
                <v-select
                    v-else
                    v-model="selectedPreset"
                    :items="savedPresetItems"
                    item-title="label"
                    item-value="value"
                    label="プリセット選択"
                    variant="outlined"
                    density="compact"
                    :menu-props="{
                      maxHeight: '350px',
                      overflow: 'scroll',
                      contentClass: 'scrollable-menu'
                    }"
                    class="draw-config-flex"
                />
                <v-btn v-track="'preset-load'" @click="loadPreset" :disabled="!selectedPreset" color="success" density="compact" class="draw-config-preset-btn">読込</v-btn>
                <v-btn v-track="'preset-delete'" @click="deletePreset" :disabled="!selectedPreset" color="error" density="compact" class="draw-config-preset-btn">削除</v-btn>
              </div>
            </div>
          </div>
        </v-tabs-window-item>

        <!-- ドロータブ -->
        <v-tabs-window-item value="draw">
          <div class="tab-panel">
            <v-switch
                class="draw-config-switch"
                v-model="drawButtonsVisible"
                @update:modelValue="onDrawButtonsVisibleChange"
                label="確定ボタン等の表示"
                color="primary"
                hide-details
            />
            <!-- フリーハンドを閉じてポリゴン（一時的にコメントアウト）
            <v-switch
                class="draw-config-switch"
                v-model="closeLineToPolygon"
                @update:modelValue="onCloseLineToPolygonChange"
                label="フリーハンドを閉じてポリゴン"
                color="primary"
                hide-details
            />
            -->
            <v-switch
                class="draw-config-switch"
                v-model="drawContinuousMode"
                @update:modelValue="onDrawContinuousModeChange"
                label="連続描画モード"
                color="primary"
                hide-details
            />
            <!-- クリック時の優先動作（一時的にコメントアウト）
            <v-select
                v-model="drawInteractionPriority"
                :items="drawInteractionPriorityItems"
                item-title="label"
                item-value="value"
                label="クリック時の優先動作"
                variant="outlined"
                density="compact"
                hide-details
                class="draw-config-compact-select oh3-mt-2"
                @update:modelValue="onDrawInteractionPriorityChange"
            />
            -->
            <v-switch
                class="draw-config-switch"
                v-model="drawAutoOpenMode"
                @update:modelValue="onDrawAutoOpenModeChange"
                label="新規作成後のウインドウを自動で開く"
                color="primary"
                hide-details
            />
            <v-switch
                class="draw-config-switch"
                v-model="thumbnailAlwaysVisible"
                @update:modelValue="onThumbnailAlwaysVisibleChange"
                label="サムネイルマーカー常時表示"
                color="primary"
                hide-details
            />
            <template v-if="drawVisibilityMaps.length > 0">
              <v-divider class="oh3-my-2" />
              <div class="oh3-text-caption oh3-mb-1">マップ別ドロー表示</div>
              <v-switch
                  v-for="map in drawVisibilityMaps"
                  :key="map.key"
                  class="draw-config-switch"
                  :model-value="drawVisiblePerMap[map.key]"
                  @update:modelValue="onDrawVisiblePerMapChange(map.key, $event)"
                  :label="map.label"
                  color="primary"
                  hide-details
              />
            </template>
          </div>
        </v-tabs-window-item>

        <!-- その他タブ -->
        <v-tabs-window-item value="other">
          <div class="tab-panel">
            <v-btn v-track="'menu-set-startup-layer'" block variant="tonal" color="primary" prepend-icon="mdi-layers" @click="setStartUrl" class="mb-2">
              起動時レイヤー設定
            </v-btn>
            <div class="hint-text mb-4">現在のレイヤーを起動時に復元</div>

            <v-btn block variant="tonal" prepend-icon="mdi-window-restore" @click="resetAllFloatingWindows" class="mb-2">
              ウィンドウ位置リセット
            </v-btn>
            <div class="hint-text mb-4">全ウィンドウの位置を初期化</div>

            <v-btn block variant="tonal" color="info" prepend-icon="mdi-party-popper" @click="showWhatsNew" class="mb-2">
              新機能のお知らせ
            </v-btn>
            <div class="hint-text mb-4">最新の新機能お知らせを再表示</div>

            <!-- 生徒クラス所属情報 -->
            <template v-if="studentClasses && studentClasses.length > 0">
              <v-divider class="my-4"></v-divider>
              <div class="subsection-label"><v-icon size="small" color="success" class="mr-1">mdi-school</v-icon>所属クラス</div>
              <div v-for="cls in studentClasses" :key="cls.class_id" class="class-item">
                <div class="class-name">{{ cls.class_name }}</div>
                <div class="class-teacher">{{ cls.teacher_school }} / {{ cls.teacher_name }}先生</div>
              </div>
            </template>
          </div>
        </v-tabs-window-item>

        <!-- 利用タブ -->
        <v-tabs-window-item value="usage">
          <div class="tab-panel">
            <!-- 未ログイン -->
            <template v-if="!userId">
              <div class="usage-empty">
                <v-icon size="48" color="grey-lighten-1">mdi-account-off</v-icon>
                <div class="usage-empty-text">ログインすると利用状況を確認できます</div>
              </div>
            </template>

            <!-- ログイン済み -->
            <template v-else>
              <!-- エラー時 -->
              <div v-if="tokenError && !tokenBalance && !tokenLoading" class="usage-empty">
                <v-icon size="48" color="grey-lighten-1">mdi-alert-circle-outline</v-icon>
                <div class="usage-empty-text">利用状況を取得できませんでした</div>
                <v-btn
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-refresh"
                  size="small"
                  class="mt-3"
                  @click="fetchTokenBalance"
                >
                  再試行
                </v-btn>
              </div>

              <!-- ローディング -->
              <div v-else-if="!tokenBalance" class="usage-loading">
                <v-progress-circular indeterminate color="primary" size="32" />
              </div>

              <!-- 残高表示（シンプル） -->
              <template v-if="tokenBalance">
                <div class="usage-plan-row">
                  <div class="usage-plan-badge">
                    <v-icon size="16" class="mr-1">{{ isTeacherPlan ? 'mdi-school' : 'mdi-star' }}</v-icon>
                    {{ planLabel }}
                  </div>
                  <!-- 教員プラン: オーナー/教員/生徒 表示 -->
                  <template v-if="isTeacherPlan">
                    <span v-if="$store.state.teacherInfo?.role === 'owner'" class="usage-role-chip admin">オーナー</span>
                    <span v-else-if="$store.state.isTeacher" class="usage-role-chip admin">教員</span>
                    <span v-else-if="$store.state.isStudent" class="usage-role-chip member">生徒</span>
                  </template>
                  <template v-else>
                    <span v-if="isOrgMember" class="usage-role-chip member">メンバー</span>
                    <span v-else-if="isOrgPlan" class="usage-role-chip admin">管理者</span>
                  </template>
                </div>

                <!-- 生徒はトークン非表示 -->
                <div v-if="$store.state.isStudent" style="font-size: var(--oh3-font-size-sm); color: var(--oh3-color-text-secondary); margin-bottom: 8px;">
                  {{ $store.state.studentClasses?.[0]?.teacher_school || '' }}
                </div>
                <div v-else class="usage-main-number">
                  <template v-if="tokenBalance.is_unlimited">
                    <span class="usage-used" style="color: #1976D2">無制限</span>
                    <span class="usage-unit">プラン</span>
                  </template>
                  <template v-else>
                    <span class="usage-used">残り </span>
                    <span class="usage-limit" :style="{ color: usageColor }">{{ displayPointBalance }}</span>
                    <span class="usage-unit">pt</span>
                  </template>
                </div>

                <!-- 組織の共有ポイント表示（教員プランは除外） -->
                <div v-if="!isTeacherPlan && (isOrgPlan || tokenBalance.is_org_member)" class="usage-org-pool" style="font-size: var(--oh3-font-size-sm); color: var(--oh3-color-text-secondary); margin-bottom: 8px;">
                  {{ orgDisplayName }}の共有ポイント
                </div>

                <!-- サブスク有効期限（有料プランのみ） -->
                <div v-if="tokenBalance.subscription_end_date" style="font-size: var(--oh3-font-size-sm); color: var(--oh3-color-text-secondary); margin-bottom: 8px;">
                  有効期限: {{ tokenBalance.subscription_end_date?.slice(0, 10) }}
                </div>

                <!-- 解約予定メッセージ -->
                <div v-if="tokenBalance.cancel_at_period_end" style="font-size: var(--oh3-font-size-sm); color: #E65100; background: #FFF3E0; padding: 8px 12px; border-radius: var(--oh3-radius-sm); margin-bottom: 8px;">
                  <v-icon size="16" color="#E65100" class="mr-1">mdi-alert</v-icon>
                  <template v-if="tokenBalance.subscription_end_date">
                    このプランは {{ tokenBalance.subscription_end_date?.slice(0, 10) }} に解約予定です
                  </template>
                  <template v-else>
                    このプランは解約予定です（契約期間終了後に体験プランに戻ります）
                  </template>
                </div>

                <!-- プランの申し込み / 変更ボタン -->
                <v-btn
                  block
                  color="primary"
                  size="large"
                  prepend-icon="mdi-arrow-up-bold-circle"
                  class="mt-4"
                  @click="showPlanDialog = true"
                >
                  {{ tokenBalance.plan_type === 'free' ? 'プランの申し込み' : 'プランの変更' }}
                </v-btn>

                <!-- サブスク管理（有料プランのみ） -->
                <v-btn
                  v-if="tokenBalance.plan_type !== 'free'"
                  block
                  variant="outlined"
                  color="primary"
                  prepend-icon="mdi-cog"
                  :loading="stripeLoading"
                  @click="handleManageSubscription"
                  class="mt-2"
                  size="small"
                >
                  サブスクリプション管理
                </v-btn>

                <!-- 組織管理（組織プランのみ） -->
                <v-btn
                  v-if="isOrgPlan && $store.state.isOrganizationOwner"
                  block
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-domain"
                  @click="openOrganizationAdmin"
                  class="mt-2"
                  size="small"
                >
                  組織管理を開く
                </v-btn>

                <!-- FAQリンク -->
                <div class="plan-faq-link mt-4">
                  <a href="faq.html" target="_blank" rel="noopener noreferrer">
                    <v-icon size="small">mdi-help-circle-outline</v-icon>
                    よくある質問（FAQ）
                  </a>
                </div>
              </template>
            </template>
          </div>
        </v-tabs-window-item>
        <!-- Androidタブ -->
        <v-tabs-window-item value="android">
          <div class="tab-panel">
            <div style="text-align: center; padding: 16px 0;">
              <v-icon size="64" color="primary">mdi-android</v-icon>
              <h3 style="margin: 12px 0 4px;">OH3 mini</h3>
              <p style="color: #666; font-size: 13px; margin-bottom: 16px;">Android版の地図ビューアです</p>
            </div>

            <v-btn
              color="primary"
              variant="elevated"
              block
              href="https://kenzkenz.jp/oh3/apk/oh3mini.apk"
              target="_blank"
              prepend-icon="mdi-download"
              class="mb-4"
            >
              APKをダウンロード
            </v-btn>

            <v-alert type="info" variant="tonal" density="compact" class="mb-3" style="font-size: 12px;">
              <div><b>インストール方法</b></div>
              <ol style="padding-left: 20px; margin-top: 4px;">
                <li>上のボタンからAPKをダウンロード</li>
                <li>ダウンロード完了後、ファイルを開く</li>
                <li>「提供元不明のアプリを許可」を有効にする（初回のみ）</li>
                <li>「インストール」をタップ</li>
              </ol>
            </v-alert>

            <v-alert type="warning" variant="tonal" density="compact" style="font-size: 12px;">
              <div><b>ご注意</b></div>
              <ul style="padding-left: 20px; margin-top: 4px;">
                <li>Android端末専用です（iPhoneは非対応）</li>
                <li>Google Play経由ではないため「提供元不明」の警告が表示されます</li>
                <li>自動アップデートはありません。新バージョンはここから再ダウンロードしてください</li>
              </ul>
            </v-alert>
          </div>
        </v-tabs-window-item>

      </v-tabs-window>
    </div>
  </FloatingWindow>

  <!-- プラン選択ダイアログ（全画面） -->
  <v-dialog v-model="showPlanDialog" fullscreen transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar color="primary">
        <v-toolbar-title class="ml-4">プランの選択</v-toolbar-title>
        <v-spacer />
        <v-btn icon color="white" @click="showPlanDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="plan-dialog-content">
        <!-- 現在のプラン表示 -->
        <div v-if="tokenBalance" class="plan-dialog-current">
          <div class="plan-dialog-current-label">現在のプラン</div>
          <div class="plan-dialog-current-plan">
            <span class="plan-dialog-current-name">{{ planLabel }}</span>
            <span v-if="!tokenBalance.is_unlimited" class="plan-dialog-current-points">
              残り {{ displayPointBalance }}pt
            </span>
            <span v-else class="plan-dialog-current-points" style="color: #1976D2">
              ポイント無制限
            </span>
          </div>
        </div>

        <!-- プラン一覧 -->
        <div class="plan-dialog-grid">
          <div
            v-for="plan in planOptions"
            :key="plan.type"
            class="plan-dialog-card"
            :class="{ 'plan-dialog-card--current': tokenBalance?.plan_type === plan.type }"
          >
            <div v-if="plan.badge" class="plan-dialog-card-badge">{{ plan.badge }}</div>
            <div class="plan-dialog-card-body">
              <div class="plan-dialog-card-icon">
                <v-icon size="32" color="primary">{{ plan.icon }}</v-icon>
              </div>
              <div class="plan-dialog-card-name">{{ plan.name }}</div>
              <div class="plan-dialog-card-price">{{ plan.price }}</div>
              <div class="plan-dialog-card-points">{{ plan.points }}</div>
              <div class="plan-dialog-card-desc">{{ plan.desc }}</div>
            </div>
            <div class="plan-dialog-card-action">
              <!-- 学校プランはGoogleフォームでの申請制 -->
              <v-btn
                v-if="tokenBalance?.plan_type !== plan.type && plan.type === 'school'"
                block
                color="primary"
                variant="tonal"
                href="https://forms.gle/VC6wycvs5DxYPyfy5"
                target="_blank"
                rel="noopener"
                prepend-icon="mdi-open-in-new"
              >
                申請フォームへ
              </v-btn>
              <v-btn
                v-else-if="tokenBalance?.plan_type !== plan.type"
                block
                color="primary"
                variant="tonal"
                :loading="stripeLoading"
                :disabled="isProduction"
                @click="handleUpgrade(plan.type)"
              >
                {{ isProduction ? '準備中' : (tokenBalance?.plan_type === 'free' ? '申し込む' : '変更する') }}
              </v-btn>
              <v-chip v-else color="primary" variant="flat">現在のプラン</v-chip>
            </div>
          </div>
        </div>

        <!-- 注意書き -->
        <div class="plan-dialog-warning">
          <v-icon size="20" class="mr-1">mdi-alert</v-icon>
          プランの内容・価格は現在未定です。正式リリースまでお待ちください。
        </div>

        <!-- 注意事項 -->
        <div class="plan-dialog-notes">
          <p>※ 年間契約は自動更新されます。解約はプラン管理画面からいつでも可能です</p>
          <p>※ 解約した場合も、契約期間終了まではプランをご利用いただけます</p>
          <p>※ ポイントは契約日から1年間有効です</p>
          <p>※ プラン変更は即時反映されます</p>
          <p>※ 席数はメンバー管理画面から招待できる人数の上限です</p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- 組織名入力ダイアログ -->
  <v-dialog v-model="orgNameDialog.show" max-width="400" persistent>
    <v-card>
      <v-card-title class="d-flex align-center" style="gap: 8px;">
        <v-icon color="primary">mdi-domain</v-icon>
        組織名の入力
      </v-card-title>
      <v-card-text>
        <p style="font-size: var(--oh3-font-size-sm); color: var(--oh3-color-text-secondary); margin-bottom: 12px;">
          組織管理画面やメンバー招待時に表示される名前です。後から変更できます。
        </p>
        <v-text-field
          v-model="orgNameDialog.value"
          label="事務所名・会社名"
          variant="outlined"
          density="compact"
          placeholder="例：○○測量事務所"
          autofocus
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="orgNameDialog.resolve('')">キャンセル</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!orgNameDialog.value.trim()"
          @click="orgNameDialog.resolve(orgNameDialog.value.trim())"
        >
          決定
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- 削除確認ダイアログ -->
  <VDialogConfirm
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :color="confirmDialog.color"
      :icon="confirmDialog.icon"
      :icon-color="confirmDialog.iconColor"
      @ok="confirmDialog.resolve(true)"
      @cancel="confirmDialog.resolve(false)"
  />
  </v-app>
</template>

<script>

import {mapillaryFilterRiset, setIsSplash, startUrl, vConfirm, changePrintMap03, printDirectionChange} from "@/js/downLoad";
import firebase from '@/firebase'
import store from "@/store";
import {mapState} from "vuex";
import FloatingWindow from '@/components/floatingwindow/FloatingWindow.vue'
import VDialogConfirm from '@/components/V-dialog/V-dialog-confirm.vue'
import { showLoginPrompt, showWarning, showError, showSuccess } from '@/js/utils/messageHelper'
import { getTokenBalance, clearBalanceCache } from '@/js/tokenService'
import { createCheckoutSession, getCustomerPortalUrl } from '@/js/stripeService'
import { phpApiPost } from '@/js/phpApi'
import {clickCircleSource} from "@/js/layers";
import {geojsonUpdate} from "@/js/pyramid";

export default {
  name: 'Dialog-menu',
  props: ['mapName'],
  components: {
    FloatingWindow,
    VDialogConfirm,
  },
  data: () => ({
    isLoggedIn: false,
    newName: '',
    message: '',
    alertType: 'info',
    layerName: '',
    joinLoading: false,
    invitedGroupName: "", // 招待されたグループ名
    isSendingInvite: false, // ローディング状態
    joinGroupId: "", // 入力されたグループID
    groupId: "",
    emailInput: "",         // 入力フォームのメールアドレス
    groupIdFromURL: "",     // URL から取得した groupId
    emailRules: [
      (v) => !!v || "メールアドレスを入力してください",
      (v) => /.+@.+\..+/.test(v) || "正しいメールアドレスを入力してください"
    ],
    inviteEmail: '',
    initialGroupName: localStorage.getItem("lastUsedGroupName") || "",
    snackbar: false,
    snackbarText: '',
    isGroupOwner: false,
    selectMenuOpen1: false,
    selectMenuOpen2: false,
    selectMenuOpen3: false,
    groupOptions: [],
    selectedGroupId: null,
    selectedGroupId2: null,
    inviteGroupId: null,
    groupName: '',
    tab: 0,
    menuTab: 'display',
    tileUrl: '',
    tileName: '',
    urlName: '',
    uid: null,
    images: [],
    nickname: '',
    email: '',
    password: '',
    errorMsg: '',
    konjyakuYear: '1890',
    addLayerDiv: false,
    loginDiv: false,
    signUpDiv: false,
    showAuthArea: false,
    s_showPcMode: localStorage.getItem('oh3_showPcMode') === 'true',
    mapSpeedOptions: [
      { value: 'verySlow', label: '超ゆっくり' },
      { value: 'slow', label: 'ゆっくり' },
      { value: 'normal', label: '標準' },
      { value: 'fast', label: '速い' },
      { value: 'veryFast', label: '最速' },
    ],
    // 印刷設定用
    titleColors: [{color:'black',label:'黒'},{color:'red',label:'赤'},{color:'blue',label:'青'},{color:'green',label:'緑'},{color:'orange',label:'オレンジ'}],
    titleDirections: [
      {direction:'a4-vertical',label:'A4縦'},
      {direction:'a4-horizontal',label:'A4横'},
      {direction:'a3-vertical',label:'A3縦'},
      {direction:'a3-horizontal',label:'A3横'}
    ],
    titleScale: 0,
    titleScales: [
      { zoom: 0.0,  label: '固定なし' },
      { zoom: 19.9, label: '1/250で固定'   },
      { zoom: 18.9, label: '1/500で固定'   },
      { zoom: 17.9, label: '1/1000で固定'  },
      { zoom: 16.9, label: '1/2000で固定'  },
      { zoom: 16.6, label: '1/2500で固定'  },
      { zoom: 15.6, label: '1/5000で固定'  },
      { zoom: 13.2, label: '1/25000で固定' },
      { zoom: 12.2, label: '1/50000で固定' },
      { zoom: 10.2, label: '1/200000で固定'},
    ],
    zoomLockHandler: null,
    configFeatureFlg: false,
    // プリセット機能用
    presetName: '',
    selectedPreset: '',
    savedPresets: [],
    // PDF Pro オプション
    pdfProShowBorder: true,
    pdfProShowNorthArrow: true,
    pdfProShowFooter: true,
    pdfProShowChibanList: false,
    pdfProAuthorName: '',
    // 描画設定用
    drawButtonsVisible: true,
    closeLineToPolygon: true,
    drawContinuousMode: false,
    drawInteractionPriority: 'snap',
    drawInteractionPriorityItems: [
      { value: 'snap', label: 'スナップ優先（新規追加）' },
      { value: 'edit', label: '編集優先（地物選択）' },
      { value: 'info', label: '情報表示優先' },
    ],
    drawAutoOpenMode: true,
    thumbnailAlwaysVisible: true,
    // トークン利用状況
    tokenLoading: false,
    tokenError: false,
    stripeLoading: false,
    showPlanDialog: false,
    tokenRefreshTimer: null,
    // 確認ダイアログ用
    confirmDialog: {
      show: false,
      title: '',
      message: '',
      color: 'primary',
      icon: null,
      iconColor: 'primary',
      resolve: () => {},
    },
    // 組織名入力ダイアログ用
    orgNameDialog: {
      show: false,
      value: '',
      resolve: () => {},
    },
  }),
  computed: {
    ...mapState([
      'isSplash',
      'map01',
      'map02',
      'mapReady',
      'myNickname',
      'clientVersion',
      'myNickname',
      'userId',
      'isSmall500',
      'isAndroid',
      'isTeacher',
      'studentClasses',
      'tokenBalance',
    ]),
    isSmallScreen() {
      return this.isSmall500
    },
    isLogin() {
      return !!this.userId;
    },
    s_isSplash: {
      get() {
        return this.$store.state.isSplash
      },
      set(value) {
        this.$store.state.isSplash = value
      }
    },
    s_dialogForOffline: {
      get() {
        return this.$store.state.dialogForOffline
      },
      set(value) {
        this.$store.state.dialogForOffline = value
      }
    },
    s_myNickname() {
      return this.$store.state.myNickname;
    },
    s_zahyokei: {
      get() {
        return this.$store.state.zahyokei
      },
      set(value) {
        this.$store.state.zahyokei = value
      }
    },
    s_isContextMenu: {
      get() {
        return this.$store.state.isContextMenu
      },
      set(value) {
        this.$store.state.isContextMenu = value
        localStorage.setItem('isContextMenu', JSON.stringify(value))
      }
    },
    s_mapillary: {
      get() {
        return this.$store.state.mapillaryFlg
      },
      set(value) {
        this.$store.state.mapillaryFlg = value
        localStorage.setItem('mapillary',value)
      }
    },
    s_mapSpeedProfile: {
      get() {
        return this.$store.state.mapSpeedProfile
      },
      set(value) {
        this.$store.state.mapSpeedProfile = value
        localStorage.setItem('mapSpeedProfile', value)
      }
    },
    s_isWindow: {
      get() {
        return this.$store.state.isWindow
      },
      set(value) {
        this.$store.state.isWindow = value
        localStorage.setItem('window',value)
      }
    },
    s_resolution: {
      get() {
        return this.$store.state.resolution
      },
      set(value) {
        this.$store.state.resolution = value
        localStorage.setItem('resolution',value)
      }
    },
    s_dialogForLink: {
      get() {
        return this.$store.state.dialogForLink
      },
      set(value) {
        this.$store.state.dialogForLink = value
      }
    },
    s_dialogForLogin: {
      get() {
        return this.$store.state.dialogForLogin
      },
      set(value) {
        this.$store.state.dialogForLogin = value
      }
    },
    s_dialogForImage: {
      get() {
        return this.$store.state.dialogForImage
      },
      set(value) {
        this.$store.state.dialogForImage = value
      }
    },
    s_extLayerName: {
      get() {
        return this.$store.state.extLayerName
      },
      set(value) {
        this.$store.state.extLayerName = value
      }
    },
    s_extLayer: {
      get() {
        return this.$store.state.extLayer
      },
      set(value) {
        this.$store.state.extLayer = value
      }
    },
    s_selectedLayers: {
      get() {
        return this.$store.state.selectedLayers
      },
      set(value) {
        this.$store.state.selectedLayers = value
      }
    },
    s_terrainLevel: {
      get() {
        return this.$store.state.terrainLevel
      },
      set(value) {
        this.$store.state.terrainLevel = value
      }
    },
    s_fetchImagesFire : {
      get() {
        return this.$store.state.fetchImagesFire
      },
      set(value) {
        this.$store.state.fetchImagesFire = value
      }
    },
    s_dialogs () {
      return this.$store.state.dialogs.menuDialog
    },
    s_isClickPointsLayer: {
      get() {
        return this.$store.state.isClickPointsLayer
      },
      set(value) {
        this.$store.state.isClickPointsLayer = value
      }
    },
    s_isPitch: {
      get() {
        return this.$store.state.isPitch
      },
      set(value) {
        this.$store.state.isPitch = value
      }
    },
    s_showCrosshair: {
      get() {
        return this.$store.state.showCrosshair
      },
      set(value) {
        this.$store.state.showCrosshair = value
        localStorage.setItem('showCrosshair', JSON.stringify(value))
      }
    },
    s_showNorthArrow: {
      get() {
        return this.$store.state.showNorthArrow
      },
      set(value) {
        this.$store.state.showNorthArrow = value
        localStorage.setItem('oh3.showNorthArrow', value ? '1' : '0')
      }
    },
    s_isFullScreenPopup: {
      get() {
        return this.$store.state.isFullScreenPopup
      },
      set(value) {
        this.$store.state.isFullScreenPopup = value
        localStorage.setItem('oh3.fullScreenPopup', value ? '1' : '0')
      }
    },
    // 印刷設定用computed
    savedPresetItems() {
      return [
        { label: 'プリセットを選択...', value: '' },
        ...this.savedPresets.map(p => ({ label: p.name, value: p.name }))
      ]
    },
    s_isPrint: {
      get() {
        return this.$store.state.isPrint
      },
      set(value) {
        return this.$store.state.isPrint = value
      }
    },
    s_printTitleText: {
      get() {
        return this.$store.state.printTitleText
      },
      set(value) {
        return this.$store.state.printTitleText = value
      }
    },
    s_textPx: {
      get() {
        return this.$store.state.textPx
      },
      set(value) {
        return this.$store.state.textPx = value
      }
    },
    s_titleColor: {
      get() {
        return this.$store.state.titleColor
      },
      set(value) {
        return this.$store.state.titleColor = value
      }
    },
    s_titleDirection: {
      get() {
        return this.$store.state.titleDirection
      },
      set(value) {
        return this.$store.state.titleDirection = value
      }
    },
    s_printDate: {
      get() {
        return this.$store.state.printDate || ''
      },
      set(value) {
        return this.$store.state.printDate = value
      }
    },
    configFeature() {
      return this.$store.state.configFeature
    },
    activePrintMaps() {
      if (this.$store.state.is4ScreenMode) return ['map01', 'map02', 'map03', 'map04']
      if (this.$store.state.map2Flg) return ['map01', 'map02']
      return ['map01']
    },
    printMapLabels() {
      if (this.$store.state.is4ScreenMode) {
        return { map01: 'タイトル (左上)', map02: 'タイトル (右上)', map03: 'タイトル (左下)', map04: 'タイトル (右下)' }
      }
      return { map01: 'タイトル (Map1)', map02: 'タイトル (Map2)', map03: 'Map3', map04: 'Map4' }
    },
    drawVisibilityMaps() {
      return [
        // { key: 'map01', label: 'Map1' },
        { key: 'map02', label: 'Map2' },
        { key: 'map03', label: 'Map3' },
        { key: 'map04', label: 'Map4' },
      ]
    },
    drawVisiblePerMap() {
      return this.$store.state.drawVisiblePerMap
    },
    isProduction() {
      return process.env.NODE_ENV === 'production'
    },
    displayPointBalance() {
      // 組織メンバーは組織プール残高を表示（消費元が組織プールなので）
      if (this.tokenBalance?.is_unlimited) return '∞'
      if (this.tokenBalance?.is_org_member && this.tokenBalance?.org_point_balance != null) {
        return this.tokenBalance.org_point_balance
      }
      return this.tokenBalance?.point_balance ?? 0
    },
    usageColor() {
      // ポイント残高ベースの色分け（組織メンバーは組織プール残高で判定）
      const tb = this.tokenBalance
      const balance = (tb?.is_org_member && tb?.org_point_balance != null)
        ? tb.org_point_balance
        : tb?.point_balance
      if (balance != null && balance <= 10) return '#F44336'  // error red
      if (balance != null && balance <= 50) return '#FF9800'  // warning orange
      return '#1976D2'  // primary blue
    },
    orgDisplayName() {
      // オーナー: organizationInfo.name、メンバー: organizationMemberInfo.org_name
      return this.$store.state.organizationInfo?.name
        || this.$store.state.organizationMemberInfo?.org_name
        || this.tokenBalance?.org_name
        || '組織'
    },
    planOptions() {
      return [
        { type: 'personal', name: 'Personal', icon: 'mdi-account', price: '年額 9,800円', points: '500pt / 年', desc: '個人利用に最適。AI画像鮮明化やAIチャットなど、基本的なAI機能をご利用いただけます。' },
        { type: 'pro', name: 'Pro', icon: 'mdi-star', price: '年額 24,800円', points: '2,000pt / 年', badge: 'おすすめ', desc: 'ヘビーユーザー向け。大量のAI処理や高頻度の利用に。Personalの4倍のポイントでコストパフォーマンスに優れています。' },
        { type: 'office', name: '事務所', icon: 'mdi-domain', price: '年額 49,800円', points: '9,000pt / 年・3席', desc: '小規模事務所向け。3名までのメンバーでポイントを共有できます。組織管理機能付き。' },
        { type: 'school', name: '学校プラン', icon: 'mdi-school', price: '年額 49,800円', points: '100,000トークン（プール）', desc: '教育機関向け。教員3名・生徒200名まで利用可能。トークンは学校全体でプール共有。ご利用には教員であることの証明が必要です。下の「申請フォームへ」からGoogleフォームでお申し込みください。' },
        { type: 'office_pro', name: '事務所Pro', icon: 'mdi-office-building', price: '年額 118,000円', points: '40,000pt / 年・10席', desc: '中規模事務所向け。10名までのメンバーで大容量ポイントを共有。チーム全体でAI機能を活用できます。' },
        { type: 'unlimited', name: '無制限', icon: 'mdi-infinity', price: '年額 248,000円', points: 'ポイント無制限・10席', desc: 'ポイント消費を気にせず、すべてのAI機能を無制限に利用できます。大規模プロジェクトや組織での本格運用に。' },
      ]
    },
    planLabel() {
      // 学校プラン（教員・生徒ロール）
      if (this.$store.state.isTeacher) return '学校プラン'
      if (this.$store.state.isStudent) return '学校プラン'

      const labels = {
        free: '体験',
        personal: 'Personal',
        pro: 'Pro',
        office: '事務所',
        school: '学校プラン',
        office_pro: '事務所Pro',
        unlimited: '無制限',
        // 旧 'teacher' plan_type の後方互換
        teacher: '学校プラン',
      }
      // 組織メンバーの場合は組織のplan_idを表示
      const planKey = this.tokenBalance?.is_org_member && this.tokenBalance?.org_plan_id
        ? this.tokenBalance.org_plan_id
        : this.tokenBalance?.plan_type
      return labels[planKey] || planKey || '体験'
    },
    isTeacherPlan() {
      return this.$store.state.isTeacher || this.$store.state.isStudent
    },
    isOrgPlan() {
      return ['office', 'office_pro', 'unlimited', 'business'].includes(this.tokenBalance?.plan_type)
    },
    isOrgMember() {
      return !!this.tokenBalance?.is_org_member
    },
  },
  methods: {
    updateMapTitle(mapName, text) {
      this.$store.state.printTitleTexts[mapName] = text
      // map01はprintTitleTextとも同期（後方互換）
      if (mapName === 'map01') {
        this.$store.state.printTitleText = text
      }
      this.configChange('title-text-' + mapName, text)
      // map01の場合は旧キーにも保存
      if (mapName === 'map01') {
        this.configChange('title-text', text)
      }
    },
    getMapLayers(mapName) {
      const layers = this.$store.state.selectedLayers[mapName] || []
      return layers.map(l => ({
        label: (l.label || '').replace(/<[^>]*>/g, ''), // HTMLタグ除去
        id: l.id
      }))
    },
    insertLayerName(mapName, layerLabel) {
      if (!layerLabel) return
      const current = this.$store.state.printTitleTexts[mapName] || ''
      const newText = current ? current + '\n' + layerLabel : layerLabel
      this.updateMapTitle(mapName, newText)
    },
    openTeacherDashboard() {
      this.$store.state.showTeacherDashboard = true
      // 基本設定ウィンドウを閉じる
      this.handleClose()
    },
    openOrganizationAdmin() {
      this.$store.commit('setShowOrganizationAdmin', true)
      this.handleClose()
    },
    handleClose() {
      this.$store.commit('setFloatingVisible', {
        id: 'menu-window-' + this.mapName,
        visible: false
      });
    },
    reloadPage() {
      location.reload()
    },
    showWhatsNew() {
      try { localStorage.removeItem('oh3.whatsNew.dismissed') } catch (_) {}
      this.$store.state.showWhatsNew = true
      this.handleClose()
    },
    onTogglePcMode(val) {
      localStorage.setItem('oh3_showPcMode', val ? 'true' : 'false')
      this.$store.commit('setShowPcMode', val)
    },
    onToggleSplash(val) {
      if (!this.isLogin) {
        showLoginPrompt()
        // 直前の状態に戻す
        this.$nextTick(() => {
          this.s_isSplash = !val
        })
        return
      }
      this.s_isSplash = val
      setIsSplash()
    },
    async setStartUrl() {
      if (!this.isLogin) {
        showLoginPrompt()
        return
      }
      const confirmed = await vConfirm(
        '現在のレイヤー状態を起動時レイヤーに設定しますか？',
        {
          title: '起動時レイヤー設定',
          okText: '設定する',
          cancelText: 'キャンセル',
          icon: 'mdi-content-save',
          color: 'primary'
        }
      )
      if (confirmed) {
        await startUrl()
      }
    },
    changePitch () {
      localStorage.setItem('isPitch',this.s_isPitch)
    },
    terrainLevelInput () {
      this.$store.state.map01.setTerrain({ 'source': 'terrain', 'exaggeration': this.s_terrainLevel })
      this.$store.state.map02.setTerrain({ 'source': 'terrain', 'exaggeration': this.s_terrainLevel })
      localStorage.setItem('terrainLevel',this.s_terrainLevel)
    },
    async resetAllFloatingWindows() {
      const confirmed = await import('@/js/downLoad').then(m => m.vConfirm(
        '全てのフローティングウィンドウの位置とサイズを初期状態に戻しますか？\n（ページ再読み込み後に反映されます）',
        {
          title: 'ウィンドウ位置リセット',
          okText: 'リセットする',
          cancelText: 'キャンセル',
          icon: 'mdi-window-restore',
          color: 'warning'
        }
      ))
      if (!confirmed) return

      // localStorage から fw: で始まるキーを全て削除
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('fw:')) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))

      this.snackbarText = `${keysToRemove.length}件のウィンドウ位置をリセットしました`
      this.snackbar = true

      // ページ再読み込み
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    },
    // 印刷設定・描画設定用メソッド
    onDrawButtonsVisibleChange() {
      localStorage.setItem('drawButtonsVisible', JSON.stringify(this.drawButtonsVisible));
      this.$store.commit('setDrawButtonsVisible', this.drawButtonsVisible);
    },
    onCloseLineToPolygonChange() {
      localStorage.setItem('closeLineToPolygon', JSON.stringify(this.closeLineToPolygon));
      this.$store.state.closeLineToPolygon = this.closeLineToPolygon;
    },
    onDrawContinuousModeChange() {
      localStorage.setItem('drawContinuousMode', JSON.stringify(this.drawContinuousMode));
      this.$store.state.drawContinuousMode = this.drawContinuousMode;
    },
    onDrawInteractionPriorityChange() {
      localStorage.setItem('drawInteractionPriority', JSON.stringify(this.drawInteractionPriority));
      this.$store.state.drawInteractionPriority = this.drawInteractionPriority;
    },
    onDrawAutoOpenModeChange() {
      localStorage.setItem('drawAutoOpenMode', JSON.stringify(this.drawAutoOpenMode));
      this.$store.state.drawAutoOpenMode = this.drawAutoOpenMode;
    },
    onThumbnailAlwaysVisibleChange() {
      this.$store.state.thumbnailAlwaysVisible = this.thumbnailAlwaysVisible
      // 既存マーカーの表示/非表示を一括切替
      this.toggleAllThumbnailMarkers(this.thumbnailAlwaysVisible)
    },
    toggleAllThumbnailMarkers(visible) {
      document.querySelectorAll('[id^="pic-marker-"]').forEach(el => {
        // ON→マーカー表示 / OFF→マーカー非表示
        el.style.display = visible ? '' : 'none'
        // ×ボタンの表示切替
        const closeBtn = el.querySelector('.pic-marker-close-btn')
        if (closeBtn) {
          closeBtn.style.display = visible ? 'none' : 'block'
        }
      })
    },
    onDrawVisiblePerMapChange(mapName, value) {
      this.$store.state.drawVisiblePerMap[mapName] = value
      localStorage.setItem('drawVisiblePerMap', JSON.stringify(this.$store.state.drawVisiblePerMap))
      // App.vueのapplyDrawLayerVisibilityを呼ぶためdrawFireをトグル
      this.$store.state.drawFire = !this.$store.state.drawFire
    },
    configChange(tgtProp, value) {
      if (!this.configFeatureFlg) {
        const map01 = this.$store.state.map01
        this.$store.state.clickCircleGeojsonText = geojsonUpdate(map01, null, clickCircleSource.iD, 'config', tgtProp, value)
        if (tgtProp === 'direction') {
          this.directionChange()
        }
      }
    },
    setZoom() {
      const map01 = this.$store.state.map01
      // まず、過去のリスナーがあれば必ず外す
      if (this.zoomLockHandler) {
        try { map01.off('zoom', this.zoomLockHandler) } catch (e) {}
        this.zoomLockHandler = null
      }
      // 解除系（titleScale=0 または 印刷モードでない）
      if (this.titleScale === 0 || !this.s_isPrint) {
        this.scaleText = ''
        return
      }
      // ここから固定 ON
      const target = this.titleScale
      map01.setZoom(target);
      this.zoomLockHandler = () => {
        map01.setZoom(target);
      };
      map01.on('zoom', this.zoomLockHandler);
      // ラベル
      const t = this.titleScales.find(t => t.zoom === target)
      this.scaleText = '縮尺：' + (t ? t.label.replace('で固定', '') : '')
    },
    directionChange() {
      // map03（2画面モード）のときだけレイアウト変更
      if (this.$store.state.printMap === 'map03') {
        changePrintMap03(this.s_titleDirection)
      }
      printDirectionChange(this.s_titleDirection)
    },
    // PDF Pro オプション変更時
    onPdfProOptionChange() {
      this.$store.state.pdfProOptions = {
        showBorder: this.pdfProShowBorder,
        showNorthArrow: this.pdfProShowNorthArrow,
        showFooter: this.pdfProShowFooter,
        showChibanList: this.pdfProShowChibanList,
        authorName: this.pdfProAuthorName,
        locationName: '', // store.state.addressを使用
      }
      // ローカルストレージに保存
      localStorage.setItem('pdfProOptions', JSON.stringify(this.$store.state.pdfProOptions))
    },
    // 確認ダイアログをプロミス化
    async showConfirm(options = {}) {
      return new Promise((resolve) => {
        this.confirmDialog = {
          show: true,
          title: options.title || '確認',
          message: options.message || '',
          color: options.color || 'primary',
          icon: options.icon || null,
          iconColor: options.iconColor || 'primary',
          resolve: (result) => {
            this.confirmDialog.show = false
            resolve(result)
          },
        }
      })
    },
    // プリセット機能
    async savePreset() {
      if (!this.presetName.trim()) {
        showWarning('プリセット名を入力してください')
        return
      }
      const preset = {
        name: this.presetName.trim(),
        settings: {
          printTitleText: this.s_printTitleText,
          printTitleTexts: { ...this.$store.state.printTitleTexts },
          textPx: this.s_textPx,
          titleColor: this.s_titleColor,
          titleDirection: this.s_titleDirection,
          titleScale: this.titleScale,
        }
      }
      const presets = JSON.parse(localStorage.getItem('printPresets') || '[]')
      const existingIndex = presets.findIndex(p => p.name === preset.name)
      if (existingIndex >= 0) {
        const confirmed = await this.showConfirm({
          title: '上書き確認',
          message: `「${preset.name}」は既に存在します。上書きしますか？`,
          color: 'warning',
          icon: 'mdi-alert',
          iconColor: 'warning',
        })
        if (!confirmed) return
        presets[existingIndex] = preset
      } else {
        presets.push(preset)
      }
      localStorage.setItem('printPresets', JSON.stringify(presets))
      this.loadPresetsList()
      this.presetName = ''
    },
    loadPreset() {
      if (!this.selectedPreset) return
      const presets = JSON.parse(localStorage.getItem('printPresets') || '[]')
      const preset = presets.find(p => p.name === this.selectedPreset)
      if (!preset) return
      // マップ別タイトル復元
      if (preset.settings.printTitleTexts) {
        Object.assign(this.$store.state.printTitleTexts, preset.settings.printTitleTexts)
      } else {
        // 旧フォーマット: map01のみ
        this.$store.state.printTitleTexts.map01 = preset.settings.printTitleText || ''
        this.$store.state.printTitleTexts.map02 = ''
        this.$store.state.printTitleTexts.map03 = ''
        this.$store.state.printTitleTexts.map04 = ''
      }
      this.s_printTitleText = this.$store.state.printTitleTexts.map01
      this.s_textPx = preset.settings.textPx
      this.s_titleColor = preset.settings.titleColor
      this.s_titleDirection = preset.settings.titleDirection
      this.titleScale = preset.settings.titleScale
    },
    async deletePreset() {
      if (!this.selectedPreset) return
      const confirmed = await this.showConfirm({
        title: '削除確認',
        message: `プリセット「${this.selectedPreset}」を削除しますか？`,
        color: 'error',
        icon: 'mdi-delete',
        iconColor: 'error',
      })
      if (!confirmed) return
      const presets = JSON.parse(localStorage.getItem('printPresets') || '[]')
      const filtered = presets.filter(p => p.name !== this.selectedPreset)
      localStorage.setItem('printPresets', JSON.stringify(filtered))
      this.selectedPreset = ''
      this.loadPresetsList()
    },
    loadPresetsList() {
      this.savedPresets = JSON.parse(localStorage.getItem('printPresets') || '[]')
    },
    async fetchTokenBalance() {
      this.tokenLoading = true
      this.tokenError = false
      try {
        clearBalanceCache()
        const result = await getTokenBalance()
        // getTokenBalanceはthrowせず { success: false } を返す場合がある
        if (!result.success && !this.tokenBalance) {
          this.tokenError = true
        }
      } catch (e) {
        console.warn('[Dialog-menu] fetchTokenBalance failed:', e)
        this.tokenError = true
      } finally {
        this.tokenLoading = false
      }
    },
    showOrgNameDialog(defaultValue = '') {
      return new Promise((resolve) => {
        this.orgNameDialog = {
          show: true,
          value: defaultValue,
          resolve: (val) => {
            this.orgNameDialog.show = false
            resolve(val)
          },
        }
      })
    },
    async recheckOrgOwner() {
      try {
        const uid = this.$store.state.user?.uid
        if (!uid) return
        const data = await phpApiPost('org_check.php', { uid })
        if (data.status === 'success' && data.isOrganizationOwner) {
          this.$store.commit('setIsOrganizationOwner', true)
          this.$store.commit('setOrganizationInfo', data.organization || data.admin)
          this.$store.state.orgGroups = data.groups || []
        }
      } catch (e) {
        console.warn('[Dialog-menu] recheckOrgOwner failed:', e)
      }
    },
    async handleUpgrade(planType = 'personal') {
      // プランラベル
      const planLabels = {
        personal: 'Personal（年額 9,800円 / 500pt）',
        pro: 'Pro（年額 24,800円 / 2,000pt）',
        office: '事務所（年額 49,800円 / 9,000pt / 3席）',
        office_pro: '事務所Pro（年額 118,000円 / 40,000pt / 10席）',
        unlimited: '無制限（年額 248,000円 / ポイント無制限 / 10席）',
      }
      const label = planLabels[planType] || planType
      const currentPlan = this.tokenBalance?.plan_type

      // 確認ダイアログ
      const isChange = currentPlan && currentPlan !== 'free'
      const confirmed = await this.showConfirm({
        title: isChange ? 'プラン変更の確認' : 'プラン申し込みの確認',
        message: isChange
          ? `<b>${label}</b> に変更します。<br>差額は日割りで精算されます。<br><br>よろしいですか？`
          : `<b>${label}</b> を申し込みます。<br>Stripeの決済画面に移動します。<br><br>よろしいですか？`,
        icon: isChange ? 'mdi-swap-horizontal' : 'mdi-arrow-up-bold',
        iconColor: 'primary',
        color: 'primary',
      })
      if (!confirmed) return

      // 組織プラン: 組織名を入力（既存組織名をデフォルト値に）
      let orgName = ''
      const orgPlans = ['office', 'office_pro', 'unlimited']
      if (orgPlans.includes(planType)) {
        const existingOrgName = this.$store.state.organizationInfo?.name || ''
        orgName = await this.showOrgNameDialog(existingOrgName)
        if (!orgName) return  // キャンセル
      }

      this.stripeLoading = true
      try {
        const result = await createCheckoutSession(planType, orgName || undefined)
        if (result.success && result.checkout_url) {
          // 新規契約 → Stripe Checkoutへリダイレクト
          window.location.href = result.checkout_url
        } else if (result.success && result.plan_changed) {
          // プラン変更完了 → 全状態をリロードで確実に反映
          const planName = this.planOptions.find(p => p.type === result.new_plan)?.name || result.new_plan
          clearBalanceCache()
          showSuccess(`プランを ${planName} に変更しました。画面を更新します...`)
          setTimeout(() => { window.location.reload() }, 1500)
        } else if (result.error === 'ALREADY_SUBSCRIBED') {
          await this.fetchTokenBalance()
          showWarning('既に同じプランが有効です。')
        } else {
          showError('Checkout Sessionの作成に失敗しました: ' + (result.error || '不明なエラー'))
        }
      } catch (e) {
        console.error('[Dialog-menu] handleUpgrade failed:', e)
        showError('エラーが発生しました')
      } finally {
        this.stripeLoading = false
      }
    },
    async handleManageSubscription() {
      this.stripeLoading = true
      try {
        const result = await getCustomerPortalUrl()
        if (result.success && result.portal_url) {
          window.location.href = result.portal_url
        } else {
          showError('Customer Portalの取得に失敗しました: ' + (result.error || '不明なエラー'))
        }
      } catch (e) {
        console.error('[Dialog-menu] handleManageSubscription failed:', e)
        showError('エラーが発生しました')
      } finally {
        this.stripeLoading = false
      }
    },
    startTokenRefresh() {
      this.stopTokenRefresh()
      this.fetchTokenBalance()
      this.tokenRefreshTimer = setInterval(() => {
        this.fetchTokenBalance()
      }, 10000) // 10秒間隔
    },
    stopTokenRefresh() {
      if (this.tokenRefreshTimer) {
        clearInterval(this.tokenRefreshTimer)
        this.tokenRefreshTimer = null
      }
    },
  },
  watch: {
    myNickname() {
      // FloatingWindow now handles the title
    },
    s_mapillary(value) {
      mapillaryFilterRiset()
      if (value) {
        store.dispatch('showFloatingWindow', 'mapillary');
      } else {
        store.dispatch('hideFloatingWindow', 'mapillary');
      }
    },
    s_isPrint(value) {
      this.setZoom()
    },
    menuTab(newVal, oldVal) {
      if (newVal === 'usage') {
        this.startTokenRefresh()
      } else if (oldVal === 'usage') {
        this.stopTokenRefresh()
      }
    },
    '$store.state.openPlanTab'(val) {
      if (val) {
        // プラン選択ダイアログを直接開く
        this.showPlanDialog = true
      }
    },
    configFeature: {
      handler(newVal, oldVal) {
        this.configFeatureFlg = true
        const props = newVal?.properties ?? {};
        // nullish coalescing を使うことで false や 0 の値も正しく扱える
        if (Object.keys(props).length === 0) {
        } else {
          this.configFeatureFlg = true
          this.$store.state.printTitleText = props['title-text'] ?? '';
          this.$store.state.textPx = props['font-size'] ?? 30;
          this.$store.state.titleColor = props['fill-color'] ?? 'black';
          this.$store.state.titleDirection = props['direction'] ?? 'a4-vertical';
          this.$store.state.drawVisible = props['visible'] ?? true;
          this.$store.state.drawOpacity = props['opacity'] ?? 1;
          setTimeout(() => {
            this.configFeatureFlg = false
          }, 2000)
        }
      },
      deep: true,
    }
  },
  mounted() {
    // localStorage から isContextMenu を読み込む
    if (localStorage.getItem('isContextMenu')) {
      this.s_isContextMenu = JSON.parse(localStorage.getItem('isContextMenu'))
    }

    if (this.isAndroid) {
      this.stopOnMenu = (e) => {
        // v-selectのドロップダウンメニューも含める
        const content = e.target.closest('.v-overlay__content.scrollable-menu, .v-overlay__content .v-list, .v-select-list, .v-overlay__content');
        if (content) e.stopPropagation();   // ← スクロールはネイティブに任せる
      };
      // 伝播を止めるには capture: true で早期にフック
      window.addEventListener('touchstart', this.stopOnMenu, { capture: true, passive: true });
      window.addEventListener('touchmove',  this.stopOnMenu, { capture: true, passive: true });
      window.addEventListener('touchend',   this.stopOnMenu, { capture: true, passive: true });
    }
    // Firebase の認証状態が確定するまで監視
    if (!store.state.isOffline) {
      firebase.auth().onAuthStateChanged(user => {
        if (user && user.email) {
          this.emailInput = user.email;
          store.state.myNickname = user.displayName || ''
          this.newName = user.displayName
          this.isLoggedIn = true
        } else {
          console.warn("⚠️ ログイン中のユーザーが見つかりません");
          this.emailInput = ""; // ログインしていない場合は空に
        }
      });
    }
    // URLパラメータからグループIDとグループ名を取得
    const params = new URLSearchParams(window.location.search);
    const groupId = params.get("group");
    const groupName = params.get("groupName");
    if (groupId) {
      this.joinGroupId = groupId;
      this.invitedGroupName = groupName || "不明なグループ";
      this.tab = "9";
      this.s_dialogForGroup = true;
    }

    if (localStorage.getItem('terrainLevel')) {
      this.s_terrainLevel = Number(localStorage.getItem('terrainLevel'))
    } else {
      this.s_terrainLevel = 1
    }
    this.s_isPitch = JSON.parse(localStorage.getItem('isPitch'))
    if (localStorage.getItem('resolution')) {
      this.s_resolution = localStorage.getItem('resolution')
    }
    if (localStorage.getItem('window')) {
      this.s_isWindow = JSON.parse(localStorage.getItem('window'))
    }
    if (localStorage.getItem('mapillary')) {
      this.s_mapillary = JSON.parse(localStorage.getItem('mapillary'))
    }
    if (localStorage.getItem('isContextMenu')) {
      this.s_isContextMenu = JSON.parse(localStorage.getItem('isContextMenu'))
    }
    const savedSpeedProfile = localStorage.getItem('mapSpeedProfile')
    if (savedSpeedProfile && this.mapSpeedOptions.some(option => option.value === savedSpeedProfile)) {
      this.s_mapSpeedProfile = savedSpeedProfile
    }
    if (localStorage.getItem('showCrosshair')) {
      this.s_showCrosshair = JSON.parse(localStorage.getItem('showCrosshair'))
    } else {
      this.s_showCrosshair = true // デフォルトは表示
    }
    // 方位記号の表示状態を復元
    this.s_showNorthArrow = localStorage.getItem('oh3.showNorthArrow') === '1'
    // 全画面ポップアップの表示状態を復元
    this.s_isFullScreenPopup = localStorage.getItem('oh3.fullScreenPopup') === '1'

    // プリセット一覧を読み込み
    this.loadPresetsList()

    // ローカルストレージから確定ボタンの表示設定を読み込み
    const savedVisible = localStorage.getItem('drawButtonsVisible');
    if (savedVisible !== null) {
      this.drawButtonsVisible = JSON.parse(savedVisible);
      this.$store.commit('setDrawButtonsVisible', this.drawButtonsVisible);
    }

    // ローカルストレージからフリーハンドを閉じてポリゴンにする設定を読み込み
    const savedCloseLineToPolygon = localStorage.getItem('closeLineToPolygon');
    if (savedCloseLineToPolygon !== null) {
      this.closeLineToPolygon = JSON.parse(savedCloseLineToPolygon);
    }
    this.$store.state.closeLineToPolygon = this.closeLineToPolygon;

    // ローカルストレージから連続描画モードの設定を読み込み
    const savedDrawContinuousMode = localStorage.getItem('drawContinuousMode');
    if (savedDrawContinuousMode !== null) {
      this.drawContinuousMode = JSON.parse(savedDrawContinuousMode);
    }
    this.$store.state.drawContinuousMode = this.drawContinuousMode;

    // ローカルストレージからクリック時の優先動作を読み込み
    const savedDrawInteractionPriority = localStorage.getItem('drawInteractionPriority');
    if (savedDrawInteractionPriority !== null) {
      this.drawInteractionPriority = JSON.parse(savedDrawInteractionPriority);
    }
    this.$store.state.drawInteractionPriority = this.drawInteractionPriority;

    // ローカルストレージから自動FW設定を読み込み
    const savedDrawAutoOpenMode = localStorage.getItem('drawAutoOpenMode');
    if (savedDrawAutoOpenMode !== null) {
      const parsed = JSON.parse(savedDrawAutoOpenMode);
      // 旧形式（'auto'/'manual'）からの移行対応
      if (typeof parsed === 'string') {
        this.drawAutoOpenMode = parsed === 'auto';
      } else {
        this.drawAutoOpenMode = parsed;
      }
    }
    this.$store.state.drawAutoOpenMode = this.drawAutoOpenMode;

    // サムネイルマーカー常時表示（URL記憶 → storeから読み込み）
    this.thumbnailAlwaysVisible = this.$store.state.thumbnailAlwaysVisible

    // ローカルストレージからマップ別ドロー表示設定を読み込み
    const savedDrawVisiblePerMap = localStorage.getItem('drawVisiblePerMap');
    if (savedDrawVisiblePerMap !== null) {
      try {
        const parsed = JSON.parse(savedDrawVisiblePerMap);
        Object.assign(this.$store.state.drawVisiblePerMap, parsed);
      } catch (e) { /* ignore */ }
    }

    // ローカルストレージからPDF Proオプションを読み込み
    const savedPdfProOptions = localStorage.getItem('pdfProOptions');
    if (savedPdfProOptions !== null) {
      try {
        const opts = JSON.parse(savedPdfProOptions);
        this.pdfProShowBorder = opts.showBorder !== false;
        this.pdfProShowNorthArrow = opts.showNorthArrow !== false;
        this.pdfProShowFooter = opts.showFooter !== false;
        this.pdfProShowChibanList = opts.showChibanList === true;
        this.pdfProAuthorName = opts.authorName || '';
        this.$store.state.pdfProOptions = opts;
      } catch (e) {
      }
    }

  },
  unmounted() {
    // トークンリフレッシュタイマーのクリーンアップ
    this.stopTokenRefresh()
    // Android用イベントリスナーのクリーンアップ
    if (this.isAndroid && this.stopOnMenu) {
      window.removeEventListener('touchstart', this.stopOnMenu, { capture: true });
      window.removeEventListener('touchmove',  this.stopOnMenu, { capture: true });
      window.removeEventListener('touchend',   this.stopOnMenu, { capture: true });
      this.stopOnMenu = null;
    }
  },
}
</script>
<style scoped>
.menu-div {
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-primary);
  background-color: var(--oh3-color-bg-default);
  box-sizing: border-box;
}

/* ヘッダー */
.menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: var(--oh3-color-gray-50);
  border-bottom: 1px solid var(--oh3-color-border-light);
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.version-badge {
  font-size: 11px;
  padding: 2px 8px;
  background: var(--oh3-color-primary);
  color: white;
  border-radius: 10px;
}
.nickname {
  font-size: 13px;
  font-weight: 600;
  color: var(--oh3-color-text-secondary);
}

/* タブ - flex: none で高さを固定 */
.menu-div :deep(.v-tabs) {
  flex: none;
}
.menu-tabs :deep(.v-tab) {
  font-size: var(--oh3-font-size-sm);
  min-width: 0;
  padding: 0 12px;
}

/* タブコンテンツ */
.tab-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.tab-panel {
  padding: 16px;
}

/* スイッチ */
.menu-switch {
  margin-bottom: 2px;
}
.menu-switch :deep(.v-label) {
  font-size: 13px;
}

/* サブセクションラベル */
.subsection-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--oh3-color-text-secondary);
  margin-bottom: 8px;
}

/* ヒントテキスト */
.hint-text {
  font-size: 11px;
  color: var(--oh3-color-text-tertiary);
  text-align: center;
}

/* クラス情報 */
.class-item {
  padding: 8px 10px;
  background: var(--oh3-color-gray-50);
  border-radius: var(--oh3-radius-sm);
  margin-bottom: 6px;
  border-left: 3px solid var(--oh3-color-success);
}
.class-item:last-child {
  margin-bottom: 0;
}
.class-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--oh3-color-text-primary);
}
.class-teacher {
  font-size: 11px;
  color: var(--oh3-color-text-tertiary);
  margin-top: 2px;
}

/* Android用セレクト */
.native-select {
  width: 100%;
  padding: 10px 12px;
  font-size: var(--oh3-font-size-base);
  border: 1px solid var(--oh3-color-border-default);
  border-radius: var(--oh3-radius-md);
  background-color: var(--oh3-color-bg-default);
  color: var(--oh3-color-text-primary);
}
.native-select:disabled {
  opacity: 0.6;
}

/* セクションヘッダー（印刷設定用） */
.section-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--oh3-color-overlay-60);
  margin-bottom: var(--oh3-spacing-2);
  text-align: left;
}

.section-header::before,
.section-header::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--oh3-color-overlay-12);
}

.section-header::before {
  margin-right: var(--oh3-spacing-3);
}

.section-header::after {
  margin-left: var(--oh3-spacing-3);
}

/* Draw config layout utilities */
.draw-config-row {
  display: flex;
  gap: 8px;
}

.draw-config-flex {
  flex: 1;
  min-width: 0;
  width: 50%;
}

/* スイッチボタンのスタイル */
.draw-config-switch {
  height: var(--oh3-input-height-base);
  margin-bottom: var(--oh3-spacing-2);
}

.draw-config-preset-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.draw-config-preset-input {
  flex: 1;
  padding: var(--oh3-spacing-2);
  border: 1px solid var(--oh3-color-border-dark);
  border-radius: var(--oh3-radius-base);
  font-size: var(--oh3-font-size-base);
}

.draw-config-preset-btn {
  margin-top: 0px;
  height: 40px;
}

/* vlike styles for font-size select (Vuetify-like custom select) */
.vlike {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--oh3-font-size-base);
  min-width: 220px;
  width: 100%;
  position: relative;
  margin-bottom: 25px;
}

.vlike__text {
  color: var(--oh3-color-overlay-60);
  opacity: 0.7;
  padding-left: var(--oh3-spacing-1);
}

.vlike__control {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 55px;
  line-height: 55px;
  padding: 0 40px 0 12px;
  border-radius: var(--oh3-radius-base);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  outline: none;
  box-shadow: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

/* コンパクトバージョン */
.vlike--compact {
  margin-bottom: 0;
}

.vlike--compact .vlike__control {
  height: 40px;
  line-height: 40px;
  font-size: var(--oh3-font-size-base);
}

.vlike--compact::after {
  top: 26px;
}

.vlike__control:hover {
  border-color: var(--oh3-color-overlay-20);
}

.vlike__control:focus {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 2px color-mix(in srgb, rgb(var(--v-theme-primary)) 25%, transparent);
}

.vlike__control[data-empty="true"] {
  color: var(--oh3-color-text-lighter);
}

.vlike__control:disabled {
  background: rgb(var(--v-theme-surface-variant));
  color: var(--oh3-color-text-lighter);
  border-color: var(--oh3-color-overlay-12);
  cursor: not-allowed;
}

.vlike::after {
  content: "";
  position: absolute;
  right: 12px;
  top: 28px;
  transform: translateY(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid currentColor;
  opacity: 0.54;
  pointer-events: none;
}

/* Floating label variant */
.vlike--floating {
  position: relative;
  padding-top: 6px;
}

.vlike--floating .vlike__text {
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: var(--oh3-font-size-sm);
  line-height: 1;
  padding: 0 6px;
  border-radius: var(--oh3-radius-lg);
  background: var(--oh3-color-bg-default);
  color: var(--oh3-color-overlay-60);
  pointer-events: none;
  text-align: left;
  z-index: 10000;
}

.vlike--floating:focus-within .vlike__text {
  color: rgb(var(--v-theme-primary));
}

.vlike--floating .vlike__control {
  margin-top: 6px;
}

.vlike--floating::after {
  top: 28px;
}

.vlike--floating.vlike--compact::after {
  top: 26px;
}

/* フォントサイズと色の横並びセレクト用（日付と同じ高さ） */
.draw-config-compact-select :deep(.v-field) {
  height: 40px !important;
  min-height: 40px !important;
  margin-top: 6px !important;
}

.draw-config-compact-select :deep(.v-field__field) {
  height: 40px !important;
  padding: 0 !important;
}

.draw-config-compact-select :deep(.v-field__input) {
  padding: 0 12px !important;
  min-height: 40px !important;
  height: 40px !important;
  line-height: 40px !important;
  font-size: var(--oh3-font-size-base) !important;
}

.draw-config-compact-select :deep(.v-field__outline) {
  height: 100% !important;
}

.draw-config-compact-select :deep(.v-label) {
  top: 8px !important;
  left: 12px !important;
  font-size: var(--oh3-font-size-sm) !important;
  line-height: 1 !important;
  padding: 0 6px !important;
  background: var(--oh3-color-bg-default) !important;
}

/* 利用タブ */
.usage-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  gap: 12px;
}
.usage-empty-text {
  font-size: 13px;
  color: var(--oh3-color-text-tertiary);
}
.usage-loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}
.usage-plan-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.usage-plan-badge {
  display: inline-flex;
  align-items: center;
  font-size: var(--oh3-font-size-sm);
  font-weight: 600;
  color: var(--oh3-color-primary);
  background: var(--oh3-color-primary-alpha-10);
  padding: 4px 12px;
  border-radius: var(--oh3-radius-xl);
}
.usage-role-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.usage-role-chip.admin {
  color: var(--oh3-color-primary, #1976D2);
  background: var(--oh3-color-primary-alpha-10);
}
.usage-role-chip.member {
  color: var(--oh3-color-text-secondary, #666);
  background: var(--oh3-color-gray-50, #f5f5f5);
}
.usage-main-number {
  text-align: center;
  margin-bottom: 12px;
  line-height: 1;
}
.usage-used {
  font-size: 36px;
  font-weight: 700;
  color: var(--oh3-color-text-primary);
}
.usage-separator {
  font-size: var(--oh3-font-size-2xl);
  color: var(--oh3-color-text-tertiary);
  margin: 0 4px;
}
.usage-limit {
  font-size: var(--oh3-font-size-2xl);
  font-weight: 500;
  color: var(--oh3-color-text-tertiary);
}
.usage-unit {
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-tertiary);
  margin-left: 4px;
}
/* FAQリンク（プランタブ末尾） */
.plan-faq-link {
  text-align: center;
  font-size: var(--oh3-font-size-sm);
}
.plan-faq-link a {
  color: var(--oh3-color-text-secondary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.plan-faq-link a:hover {
  color: var(--oh3-color-primary);
  text-decoration: underline;
}

/* プラン選択ダイアログ */
.plan-dialog-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px;
}
.plan-dialog-current {
  text-align: center;
  margin-bottom: 32px;
  padding: 16px;
  background: var(--oh3-color-primary-alpha-10);
  border-radius: var(--oh3-radius-lg, 12px);
}
.plan-dialog-current-label {
  font-size: var(--oh3-font-size-sm, 13px);
  color: var(--oh3-color-text-secondary, #666);
  margin-bottom: 4px;
}
.plan-dialog-current-plan {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
}
.plan-dialog-current-name {
  font-size: 24px;
  font-weight: 700;
  color: var(--oh3-color-primary, #1976D2);
}
.plan-dialog-current-points {
  font-size: 18px;
  font-weight: 500;
  color: var(--oh3-color-text-primary, #333);
}
.plan-dialog-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}
.plan-dialog-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 2px solid var(--oh3-color-border-light, #e0e0e0);
  border-radius: var(--oh3-radius-lg, 12px);
  padding: 24px 20px;
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}
.plan-dialog-card:hover {
  border-color: var(--oh3-color-primary, #1976D2);
  box-shadow: var(--oh3-shadow-md, 0 4px 12px rgba(0,0,0,0.1));
  transform: translateY(-2px);
}
.plan-dialog-card--current {
  border-color: var(--oh3-color-primary, #1976D2);
  background: var(--oh3-color-primary-alpha-10);
}
.plan-dialog-card-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--oh3-color-primary, #1976D2);
  color: var(--oh3-color-text-inverse);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 12px;
  border-radius: 10px;
}
.plan-dialog-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.plan-dialog-card-icon {
  margin-bottom: 12px;
}
.plan-dialog-card-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--oh3-color-text-primary, #333);
  margin-bottom: 4px;
}
.plan-dialog-card-price {
  font-size: 18px;
  font-weight: 600;
  color: var(--oh3-color-primary, #1976D2);
  margin-bottom: 4px;
}
.plan-dialog-card-points {
  font-size: 14px;
  font-weight: 500;
  color: var(--oh3-color-text-secondary, #666);
  margin-bottom: 12px;
}
.plan-dialog-card-desc {
  font-size: 13px;
  color: var(--oh3-color-text-secondary, #666);
  line-height: 1.5;
  text-align: left;
}
.plan-dialog-card-action {
  padding-top: 16px;
  display: flex;
  justify-content: center;
}
.plan-dialog-warning {
  margin-top: 24px;
  padding: 12px 16px;
  background: rgba(211, 47, 47, 0.08);
  border: 1px solid rgba(211, 47, 47, 0.3);
  border-radius: var(--oh3-radius-md, 8px);
  color: var(--oh3-color-error);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.plan-dialog-notes {
  margin-top: 16px;
  padding: 16px;
  background: var(--oh3-color-gray-50, #f5f5f5);
  border-radius: var(--oh3-radius-md, 8px);
}
.plan-dialog-notes p {
  font-size: 12px;
  color: var(--oh3-color-text-tertiary, #999);
  margin: 4px 0;
}
@media (max-width: 1024px) {
  .plan-dialog-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 600px) {
  .plan-dialog-grid {
    grid-template-columns: 1fr;
  }
  .plan-dialog-card {
    padding: 16px 14px;
  }
}
</style>
